import ResultPage from './ResultPage';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import RecategorizationPDF from './RecategorizationPDF';
import Button from 'Components/DesignSystem/Button/Button';

type TRecategorizationWrapperProps = {
    setShowResult: React.Dispatch<React.SetStateAction<boolean>>
    modalRecategorizeClose: () => void
    payload: TRecategorizePayload
    recategorizationResponse: RecategorizationResponse
}


/**
 * The download glyph. It was paired with a hand-rolled `tw-animate-spin` SVG for the
 * in-flight state; DS `Button loading` supplies that spinner, so only the arrow is
 * left. It already strokes in `currentColor`, so it takes the button's ink.
 */
const DownloadGlyph = () => (
    <svg className="tw-w-5 tw-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
);

const RecategorizationWrapper = ({ setShowResult, modalRecategorizeClose, payload, recategorizationResponse }: TRecategorizationWrapperProps) => {

    const [attributes, setAttributes] = useState<TOption[]>()    

    const extractAndComposeAttributeList = () => {
        const getAttributesToAddValues = recategorizationResponse?.unique_attr_target_list?.filter((row, index) => {
            return !payload.unique_attr_source_list.find(
                attr => attr.target_attribute_id === row?.attribute_id && attr.checked
            );
        }).map(x => {
            return {
                id: x?.attribute_id, 
                name: x?.attribute_name
            }
        })
        setAttributes(getAttributesToAddValues)
    }

    useEffect(() => {
        extractAndComposeAttributeList()
    }, [payload?.unique_attr_source_list])

    const [loading, setLoading] = useState<boolean>(false);
    const pdfRef = useRef<HTMLDivElement>(null);

    const handleGenerateAndDownload = async () => {
        setLoading(true);

        try {
            const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
                import('jspdf'),
                import('html2canvas'),
            ]);

            const content = pdfRef.current;
            if (!content) return;

            const originalStyle = content.style.display;
            content.style.display = 'block';

            const canvas = await html2canvas(content, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff',
                logging: true,
                onclone: (document, element) => {
                    element.style.display = 'block';
                }
            });

            const imgWidth = 210;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            const pdf = new jsPDF('p', 'mm', 'a4');
            pdf.addImage(
                canvas.toDataURL('image/png'),
                'PNG',
                0,
                0,
                imgWidth,
                imgHeight
            );

            pdf.save(`recategorization_${new Date().toISOString().split('T')[0]}.pdf`);
            content.style.display = originalStyle;
            toast.success("Recategorization results downloaded successfully")
            setShowResult(false);
            modalRecategorizeClose()
        } catch (error) {
            console.error('Error generating PDF:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="tw-relative">
            <ResultPage attributes={attributes as TOption[]}
            />
            <div className="pdf-container" style={{ position: 'absolute', left: '-9999px' }}>
                <div 
                    ref={pdfRef}
                    style={{
                        width: '800px',
                        background: 'white',
                        padding: '40px',
                        display: 'none'
                    }}
                >
                    <RecategorizationPDF
                        sourceName={payload?.source_category_name as string}
                        targetName={payload?.target_category_name as string}
                        totalProducts={payload?.product_list?.length as number}
                        attributes={attributes as TOption[]}
                    />
                </div>
            </div>
            <div className="tw-fixed tw-bottom-6 tw-right-6 tw-z-50">
                {/*
                  * `loading` IS the disable — Button computes `isDisabled = disabled || loading`,
                  * the same expression `disabled={loading}` spelled out. The label swap stays in
                  * the caller: "Generating PDF..." is an observable this screen's spec pins.
                  */}
                <Button
                    aria-label="Download"
                    variant="primary"
                    onClick={handleGenerateAndDownload}
                    data-testid="recategorize-generate-download-btn"
                    loading={loading}
                    icon={<DownloadGlyph />}
                    iconPosition="start"
                    className="tw-shadow-lg hover:tw-shadow-xl"
                >
                    {loading ? 'Generating PDF...' : 'Download PDF and Close'}
                </Button>
            </div>
        </div>
    );
};

export default RecategorizationWrapper;